var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { reportsApi } from "@api/enpoints/reports-api";
export const nodeStatusFilterOptions = [
    { value: "All", label: "All" },
    { value: "Active", label: "Active" },
    { value: "InActive", label: "InActive" },
];
// Total date range options (period = how far back from startDate)
export const getPeriodOptions = (t) => [
    { value: "31d", label: `31 ${t("common:labels.days")}` },
    { value: "14d", label: `14 ${t("common:labels.days")}` },
    { value: "7d", label: `7 ${t("common:labels.days")}` },
    { value: "5d", label: `5 ${t("common:labels.days")}` },
    { value: "2d", label: `2 ${t("common:labels.days")}` },
    { value: "24h", label: `24 ${t("common:labels.hours")}` },
    { value: "12h", label: `12 ${t("common:labels.hours")}` },
    { value: "6h", label: `6 ${t("common:labels.hours")}` },
    { value: "3h", label: `3 ${t("common:labels.hours")}` },
    { value: "2h", label: `2 ${t("common:labels.hours")}` },
    { value: "1h", label: `1 ${t("common:labels.hour")}` },
    { value: "30min", label: `30 ${t("common:labels.minutes")}` },
];
// Block/interval size options (showBy = how to group the data)
export const getShowByOptions = (t) => [
    { value: "24h", label: `24 ${t("common:labels.hours")}` },
    { value: "12h", label: `12 ${t("common:labels.hours")}` },
    { value: "8h", label: `8 ${t("common:labels.hours")}` },
    { value: "6h", label: `6 ${t("common:labels.hours")}` },
    { value: "4h", label: `4 ${t("common:labels.hours")}` },
    { value: "3h", label: `3 ${t("common:labels.hours")}` },
    { value: "2h", label: `2 ${t("common:labels.hours")}` },
    { value: "1h", label: `1 ${t("common:labels.hour")}` },
    { value: "30min", label: `30 ${t("common:labels.minutes")}` },
    { value: "15min", label: `15 ${t("common:labels.minutes")}` },
];
const DEFAULT_PERIOD = "24h";
const DEFAULT_SHOW_BY = "1h";
// Large enough that the first page always overflows the listbox, so the
// dropdown is scrollable (and therefore pageable) without extra round-trips.
const NODE_PAGE_SIZE = 20;
const getIntervalFromShowBy = (showBy) => {
    var _a;
    const map = {
        "24h": "24 Hours",
        "12h": "12 Hours",
        "8h": "8 Hours",
        "6h": "6 Hours",
        "4h": "4 Hours",
        "3h": "3 Hours",
        "2h": "2 Hours",
        "1h": "1 Hour",
        "30min": "30 Minutes",
        "15min": "15 Minutes",
    };
    return (_a = map[showBy]) !== null && _a !== void 0 ? _a : "1 Hour";
};
const addPeriodToDate = (from, period) => {
    const end = new Date(from);
    switch (period) {
        case "31d":
            end.setDate(end.getDate() + 31);
            break;
        case "14d":
            end.setDate(end.getDate() + 14);
            break;
        case "7d":
            end.setDate(end.getDate() + 7);
            break;
        case "5d":
            end.setDate(end.getDate() + 5);
            break;
        case "2d":
            end.setDate(end.getDate() + 2);
            break;
        case "24h":
            end.setHours(end.getHours() + 24);
            break;
        case "12h":
            end.setHours(end.getHours() + 12);
            break;
        case "6h":
            end.setHours(end.getHours() + 6);
            break;
        case "3h":
            end.setHours(end.getHours() + 3);
            break;
        case "2h":
            end.setHours(end.getHours() + 2);
            break;
        case "1h":
            end.setHours(end.getHours() + 1);
            break;
        case "30min":
            end.setMinutes(end.getMinutes() + 30);
            break;
        default:
            end.setHours(end.getHours() + 24);
    }
    return end;
};
const mapItemsToOptions = (items) => items.map((i) => ({
    value: i.nodeUid,
    label: `${i.nodeName} (${i.accessPointName})`,
    isActive: i.isActive,
}));
export const useReportsFilter = () => {
    const defaultStartDate = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const [nodeOptions, setNodeOptions] = useState([]);
    const [nodesLoading, setNodesLoading] = useState(false);
    const [nodesHasMore, setNodesHasMore] = useState(true);
    const nodesPageRef = useRef(1);
    const nodesSearchRef = useRef("");
    const fetchingNodesRef = useRef(false);
    const activeNodesQueryRef = useRef(null);
    const latestRequestRef = useRef(0);
    const hasInitialized = useRef(false);
    const prevNodesKeyRef = useRef("");
    const { control, watch, setValue, reset } = useForm({
        defaultValues: {
            nodes: [],
            nodeStatus: "All",
            showBy: DEFAULT_SHOW_BY,
            startDate: defaultStartDate,
            startTime: null,
            period: DEFAULT_PERIOD,
        },
    });
    const selectedNodes = watch("nodes");
    const nodeStatus = watch("nodeStatus");
    const showBy = watch("showBy");
    const startDate = watch("startDate");
    const startTime = watch("startTime");
    const period = watch("period");
    const loadNodesPage = useCallback((page, append) => __awaiter(void 0, void 0, void 0, function* () {
        const queryKey = JSON.stringify({
            nodeStatus: nodeStatus || "All",
            search: nodesSearchRef.current,
            page,
        });
        // Skip duplicate page loads and StrictMode replays of the same query.
        // A different search/reset query can supersede an in-flight request.
        if (append && fetchingNodesRef.current)
            return;
        if (fetchingNodesRef.current &&
            activeNodesQueryRef.current === queryKey) {
            return;
        }
        const requestId = ++latestRequestRef.current;
        fetchingNodesRef.current = true;
        activeNodesQueryRef.current = queryKey;
        setNodesLoading(true);
        try {
            const res = yield reportsApi.getReportNodesDropdown({
                nodeStatus: nodeStatus || "All",
                search: nodesSearchRef.current,
                pageNumber: page,
                pageSize: NODE_PAGE_SIZE,
            });
            if (requestId !== latestRequestRef.current)
                return; // stale response
            if (res.isSuccess && res.data) {
                const mapped = mapItemsToOptions(res.data.items);
                setNodeOptions((prev) => {
                    if (!append)
                        return mapped;
                    const map = new Map();
                    for (const o of prev)
                        map.set(String(o.value), o);
                    for (const o of mapped)
                        map.set(String(o.value), o);
                    return Array.from(map.values());
                });
                setNodesHasMore(res.data.hasMore);
                nodesPageRef.current = page;
            }
            else {
                if (!append)
                    setNodeOptions([]);
                setNodesHasMore(false);
            }
        }
        finally {
            if (requestId === latestRequestRef.current) {
                setNodesLoading(false);
                fetchingNodesRef.current = false;
                activeNodesQueryRef.current = null;
            }
        }
    }), [nodeStatus]);
    const resetFilters = useCallback(() => {
        hasInitialized.current = true;
        nodesSearchRef.current = "";
        nodesPageRef.current = 1;
        setNodesHasMore(true);
        reset({
            nodes: [],
            nodeStatus: "All",
            showBy: DEFAULT_SHOW_BY,
            startDate: defaultStartDate,
            startTime: null,
            period: DEFAULT_PERIOD,
        });
        void loadNodesPage(1, false);
    }, [defaultStartDate, loadNodesPage, reset]);
    useEffect(() => {
        nodesSearchRef.current = "";
        nodesPageRef.current = 1;
        setNodesHasMore(true);
        setValue("nodes", []);
        hasInitialized.current = false;
        void loadNodesPage(1, false);
    }, [nodeStatus, setValue, loadNodesPage]);
    const handleNodesSearch = useCallback((term) => {
        nodesSearchRef.current = term;
        nodesPageRef.current = 1;
        setNodesHasMore(true);
        void loadNodesPage(1, false);
    }, [loadNodesPage]);
    const loadNextNodesPage = useCallback(() => {
        if (nodesLoading || !nodesHasMore || fetchingNodesRef.current)
            return;
        void loadNodesPage(nodesPageRef.current + 1, true);
    }, [nodesLoading, nodesHasMore, loadNodesPage]);
    const handleNodesListboxScroll = useCallback((e) => {
        const t = e.currentTarget;
        if (t.scrollTop + t.clientHeight >= t.scrollHeight - 48) {
            loadNextNodesPage();
        }
    }, [loadNextNodesPage]);
    // Auto-select first node once options load
    useEffect(() => {
        if (!hasInitialized.current &&
            nodeOptions.length > 0 &&
            selectedNodes.length === 0) {
            hasInitialized.current = true;
            setValue("nodes", [nodeOptions[0]]);
        }
    }, [nodeOptions, selectedNodes.length, setValue]);
    // Clear date/period when the user changes node selection (not on initial auto-select)
    useEffect(() => {
        const currentKey = selectedNodes.map((n) => String(n.value)).join(",");
        if (hasInitialized.current &&
            prevNodesKeyRef.current !== "" &&
            prevNodesKeyRef.current !== currentKey) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            setValue("startDate", today);
            setValue("startTime", null);
            setValue("period", DEFAULT_PERIOD);
            setValue("showBy", DEFAULT_SHOW_BY);
        }
        prevNodesKeyRef.current = currentKey;
    }, [selectedNodes, setValue]);
    const fromDate = useMemo(() => {
        const d = startDate ? new Date(startDate) : new Date(defaultStartDate);
        d.setSeconds(0, 0);
        if (startTime) {
            const t = new Date(startTime);
            d.setHours(t.getHours(), t.getMinutes(), 0, 0);
        }
        else {
            d.setHours(0, 0, 0, 0);
        }
        return d;
    }, [startDate, startTime, defaultStartDate]);
    const toDate = useMemo(() => addPeriodToDate(fromDate, period || DEFAULT_PERIOD), [fromDate, period]);
    const interval = useMemo(() => getIntervalFromShowBy(showBy || DEFAULT_SHOW_BY), [showBy]);
    return {
        control,
        watch,
        setValue,
        reset,
        resetFilters,
        selectedNodes,
        nodeStatus,
        showBy: interval,
        period,
        interval,
        startDate,
        startTime,
        fromDate,
        toDate,
        nodeOptions,
        nodesLoading,
        nodesHasMore,
        handleNodesSearch,
        handleNodesListboxScroll,
        loadNextNodesPage,
    };
};
